import Vue from 'vue';
import { Module } from 'vuex';
import { RootState } from '@/types/store';

export interface InviteState {
    invite: ListWrap<Invite.InviteItem> & ListRequesting;
    stat: {
        /** 包括sign_up_count */
        affect_count: number;
        sign_up_count: number;
        total_money: number;
        total_coin: number;
        /** nothing 没有图片，unclaimed 待领取，received 查看订单 */
        gift_status: 'nothing' | 'unclaimed' | 'received', // ['nothing', 'unclaimed', 'received']
    } | null;
    gift: Invite.Gift[] | null;
    /** top10 */
    top10: string[] | null;
    stat13: {
        invite_count: number, // 邀请人数
        gift_selected: boolean, // 是否已选礼品
        gift_id: number, // 已选的礼品id
    } | null;

    monitorInviteRank: {
        [activityId: number]: ListWrap<Invite.MonitorInviteRank> & ListRequesting;
    };

    dreamInvitePrize: {
        [activityId: number]: ListWrap<Invite.DreamInvitePrize> & ListRequesting;
    };
}

const m: Module<InviteState, RootState> = {
    namespaced: true,
    state: () => ({
        gift: null,
        invite: {
            list: [],
            loaded: false,
            requesting: false,
        },
        stat: null,
        top10: null,
        stat13: null,
        monitorInviteRank: {},
        dreamInvitePrize: {},
    }),
    mutations: {
        setGift(state, gift) {
            state.gift = gift;
        },
        concatList(state, list) {
            state.invite.list.push(...list);
        },
        setListLoaded(state, loaded) {
            state.invite.loaded = loaded;
        },
        setListReq(state, req) {
            state.invite.requesting = req;
        },
        setStat(state, stat) {
            state.stat = stat;
        },
        setTop10(state, top10) {
            state.top10 = top10;
        },
        setStat13(state, stat) {
            state.stat13 = stat;
        },
        /** 队长邀请排行榜 */
        initMonitorInviteRank(state, { key }) {
            Vue.set(state.monitorInviteRank, key, {
                list: [],
                loaded: false,
                requesting: false,
            });
        },
        concatMonitorInviteRank(state, { key, list }) {
            state.monitorInviteRank[key].list = state.monitorInviteRank[key].list.concat(list);
        },
        setMonitorInviteRankLoaded(state, { key, loaded }) {
            state.monitorInviteRank[key].loaded = loaded;
        },
        setMonitorInviteRankReq(state, { key, req }) {
            state.monitorInviteRank[key].requesting = req;
        },

        /** 追梦中国邀请奖金列表 */
        initDreamInvitePrize(state, { key }) {
            Vue.set(state.dreamInvitePrize, key, {
                list: [],
                loaded: false,
                requesting: false,
            });
        },
        concatDreamInvitePrize(state, { key, list }) {
            state.dreamInvitePrize[key].list = state.dreamInvitePrize[key].list.concat(list);
        },
        setDreamInvitePrizeLoaded(state, { key, loaded }) {
            state.dreamInvitePrize[key].loaded = loaded;
        },
        setDreamInvitePrizeReq(state, { key, req }) {
            state.dreamInvitePrize[key].requesting = req;
        },
    },
    actions: {
        /**
         * 获得礼品
         */
        async getGift({ state, commit }, { activityId }) {
            const response = await Vue.axios.get('/api/v1/invite/gift_list', {
                params: {
                    id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setGift', rdata.data);
            }
            return rdata;
        },
        /**
         * 获得邀请列表
         */
        async getList({ state, commit }, { activityId, lastId, count }) {
            if (state.invite.requesting || state.invite.loaded) {
                return;
            }

            commit('setListReq', true);
            const response = await Vue.axios.get('/api/v1/invite/list', {
                params: {
                    id: activityId,
                    last_id: lastId,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const list = rdata.data;
                commit('concatList', list);
                if (list.length < count) {
                    commit('setListLoaded', true);
                }
            }
            commit('setListReq', false);
            return rdata;
        },
        /**
         * 获取邀请统计
         */
        async getStat({ state, commit }, { activityId }) {
            const response = await Vue.axios.get('/api/v1/invite/stats', {
                params: {
                    id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setStat', rdata.data);
            }
            return rdata;
        },
        async getTop10({ state, commit }, { activityId }) {
            const response = await Vue.axios.get('/api/v1/invite/top_10', {
                params: {
                    id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setTop10', rdata.data);
            }
            return rdata;
        },
        /**
         * 获取邀请第13届百日跑
         */
        async getStat13({ state, commit }, { activityId, forceUpdate = false }) {
            if (state.stat13 && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/invite/activity_invite_stat', {
                params: {
                    activity_id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setStat13', rdata.data);
            }
            return rdata;
        },

        /**
         * 提交领取13届礼品的请求
         */
        async chooseActivityGift13({ state, commit }, { activityId, giftId }) {
            const response = await Vue.axios.post('/api/v1/invite/choose_activity_gift', {
                activity_id: activityId,
                id: giftId,
            });
            const rdata = response.data;
            return rdata;
        },
        /**
         * 队长邀请排行榜
         */
        async getMonitorInviteRank({ state, commit }, { activityId, offset, count, forceUpdate }) {
            const key = activityId;
            let wrap = state.monitorInviteRank[key];
            if (!wrap || (offset === 0 && forceUpdate)) {
                commit('initMonitorInviteRank', { key });
                wrap = state.monitorInviteRank[key];
            }
            if (wrap.requesting || wrap.loaded) {
                return;
            }
            const end = offset + count;
            const len = wrap.list.length;
            if (end <= len) {
                return;
            }
            if (offset < len) {
                offset = len;
                count = end - offset;
            }
            commit('setMonitorInviteRankReq', { key, req: true });
            const response = await Vue.axios.get('/api/v1/invite/monitor_invite_rank', {
                params: {
                    id: activityId,
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const list = rdata.data.list;
                commit('concatMonitorInviteRank', { key, list });
                if (list.length < count) {
                    commit('setMonitorInviteRankLoaded', { key, loaded: true });
                }
            }
            commit('setMonitorInviteRankReq', { key, req: false });
            return rdata;
        },
        /**
         * 追梦中国个人邀请列表
         */
        async getInvitePrizeList({ state, commit }, { activityId, offset, count, forceUpdate }) {
            const key = Number(activityId);
            let wrap = state.dreamInvitePrize[key];
            if (!wrap || (offset === 0 && forceUpdate)) {
                commit('initDreamInvitePrize', { key });
                wrap = state.dreamInvitePrize[key];
            }
            if (wrap.requesting || wrap.loaded) {
                return;
            }
            const end = offset + count;
            const len = wrap.list.length;
            if (end <= len) {
                return;
            }
            if (offset < len) {
                offset = len;
                count = end - offset;
            }
            commit('setDreamInvitePrizeReq', { key, req: true });
            const response = await Vue.axios.get('/api/v1/invite/prize', {
                params: {
                    activity_id: activityId,
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const list = rdata.data;
                commit('concatDreamInvitePrize', { key, list });
                if (list.length < count) {
                    commit('setDreamInvitePrizeLoaded', { key, loaded: true });
                }
            }
            commit('setDreamInvitePrizeReq', { key, req: false });
            return rdata;
        },
        // 领取礼品
        async receiveGift({ state, commit }, {  activityId }: { activityId: number }) {
            const response = await Vue.axios.get('/api/v1/invite/receive_gift', {
                params: {
                    activity_id: activityId,
                },
            });
            const rdata = response.data;
            return rdata;
        },
    },
};

export default m;
